import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { CheckCircle, AlertCircle, Brain, Loader2, Building2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PublicAssessment = () => {
  const { token } = useParams();
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    fetchAssessment();
  }, [token]);

  const fetchAssessment = async () => {
    try {
      const response = await axios.get(`${API_URL}/public/assessment/${token}`);
      // API returns { assessment: {...}, employee: {...}, company_name: "..." }
      const data = response.data;
      setAssessment({
        ...data.assessment,
        company_name: data.company_name,
        employee: data.employee,
        already_responded: data.already_responded,
        // Map perguntas from assessment
        perguntas: data.assessment?.perguntas || [],
        titulo: data.assessment?.titulo || data.assessment?.title || 'Avaliação'
      });
      
      // Initialize answers - ALL start as null (no pre-selection)
      const initialAnswers = {};
      const perguntas = data.assessment?.perguntas || [];
      perguntas.forEach((p) => {
        if (p.tipo === 'escala_1_5') {
          initialAnswers[p.id] = null;  // No pre-selection
        } else if (p.tipo === 'escala_1_4') {
          initialAnswers[p.id] = null;  // No pre-selection
        } else if (p.tipo === 'sim_nao') {
          initialAnswers[p.id] = null;
        } else if (p.tipo === 'multipla_escolha') {
          initialAnswers[p.id] = null;
        } else {
          initialAnswers[p.id] = '';
        }
      });
      setAnswers(initialAnswers);
    } catch (error) {
      setError(error.response?.data?.detail || 'Erro ao carregar avaliação');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required questions - check ALL required fields including scales
    const unanswered = assessment.perguntas.filter(p => {
      if (!p.obrigatoria) return false;
      const answer = answers[p.id];
      // Scale questions (1-4 and 1-5) must have a value selected
      if ((p.tipo === 'escala_1_4' || p.tipo === 'escala_1_5') && answer === null) return true;
      if (p.tipo === 'sim_nao' && answer === null) return true;
      if (p.tipo === 'multipla_escolha' && answer === null) return true;
      if (p.tipo === 'texto_livre' && !answer?.trim()) return true;
      return false;
    });

    if (unanswered.length > 0) {
      setError('Por favor, responda todas as perguntas obrigatórias.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const respostas = Object.entries(answers).map(([question_id, resposta]) => ({
        question_id,
        resposta
      }));

      await axios.post(`${API_URL}/public/assessment/${token}/respond`, {
        token,
        respostas
      });

      setSuccess(true);
    } catch (error) {
      setError(error.response?.data?.detail || 'Erro ao enviar respostas');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={48} className="animate-spin text-emerald-500 mx-auto mb-4" />
          <p className="text-slate-600">Carregando avaliação...</p>
        </div>
      </div>
    );
  }

  if (error && !assessment) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="p-8 max-w-md text-center">
          <AlertCircle size={64} className="text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Oops!</h1>
          <p className="text-slate-600">{error}</p>
        </Card>
      </div>
    );
  }

  // Show already responded message (token already used)
  if (assessment?.already_responded) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Helmet>
          <title>Avaliação Já Respondida | CERP Brasil NR1</title>
        </Helmet>
        <Card className="p-8 max-w-md text-center">
          <CheckCircle size={64} className="text-emerald-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Obrigado!</h1>
          <p className="text-slate-600 mb-4">
            Sua resposta para esta avaliação já foi registrada com sucesso.
          </p>
          <p className="text-sm text-slate-500 mb-4">
            Cada avaliação pode ser respondida apenas uma vez para garantir a integridade dos dados.
          </p>
          <div className="mt-4 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
            <p className="text-emerald-700 text-sm">
              Suas respostas são anônimas e confidenciais, protegidas pela LGPD.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  // Show success screen after submission
  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Helmet>
          <title>Avaliação Concluída | CERP Brasil NR1</title>
        </Helmet>
        <Card className="p-8 max-w-md text-center">
          <CheckCircle size={64} className="text-emerald-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Obrigado!</h1>
          <p className="text-slate-600 mb-4">
            Suas respostas foram registradas de forma anônima.
          </p>
          <p className="text-sm text-slate-500">
            Sua participação é muito importante para melhorar o ambiente de trabalho.
          </p>
        </Card>
      </div>
    );
  }

  const companyName = assessment?.company_name || 'Empresa';
  const assessmentTitle = assessment?.titulo || assessment?.title || 'Avaliação de SST';
  const ogDescription = `Responda a avaliação de Segurança e Saúde no Trabalho da ${companyName}. Suas respostas são confidenciais.`;
  const pageTitle = assessmentTitle + ' | ' + companyName + ' - CERP Brasil NR1';
  const ogTitle = assessmentTitle + ' | CERP Brasil NR1';

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <Helmet>
        <title>{pageTitle}</title>
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDescription} />
        <meta name="description" content={ogDescription} />
      </Helmet>
      <div className="max-w-2xl mx-auto">
        {/* Company Name Header */}
        {assessment?.company_name && (
          <div className="flex items-center justify-center gap-2 mb-4 text-teal-600">
            <Building2 className="h-5 w-5" />
            <span className="font-semibold text-lg">{assessment.company_name}</span>
          </div>
        )}
        
        {/* Header */}
        <Card className="p-6 mb-6 bg-gradient-to-r from-slate-900 to-slate-800 text-white">
          <div className="flex items-center gap-4 mb-4">
            <div className="bg-emerald-500 p-3 rounded-lg">
              <Brain size={32} />
            </div>
            <div>
              <h1 className="text-2xl font-bold">CERP Brasil NR1</h1>
              <p className="text-slate-300 text-sm">Avaliação de Saúde e Segurança no Trabalho</p>
            </div>
          </div>
          <h2 className="text-xl font-semibold mb-2">{assessment?.titulo}</h2>
          {assessment?.descricao && (
            <p className="text-slate-300">{assessment.descricao}</p>
          )}
          {assessment?.data_fim && (
            <p className="text-sm text-yellow-300 mt-3">
              Prazo: {new Date(assessment.data_fim).toLocaleDateString('pt-BR')}
            </p>
          )}
        </Card>

        {/* Instructions */}
        <Card className="p-4 mb-6 bg-blue-50 border-blue-200">
          <h3 className="font-semibold text-blue-900 mb-2">Instruções</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Esta avaliação é <strong>completamente anônima</strong></li>
            <li>• Suas respostas não serão associadas ao seu nome</li>
            <li>• Responda com sinceridade para ajudar a melhorar o ambiente de trabalho</li>
            <li>• Perguntas marcadas com * são obrigatórias</li>
          </ul>
        </Card>

        {/* Error Message */}
        {error && (
          <Card className="p-4 mb-6 bg-red-50 border-red-200">
            <p className="text-red-600">{error}</p>
          </Card>
        )}

        {/* Questions Form */}
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {assessment?.perguntas?.map((pergunta, index) => (
              <Card key={pergunta.id} className="p-6" data-testid={`question-${pergunta.id}`}>
                <div className="flex items-start gap-3 mb-4">
                  <span className="bg-emerald-500 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm">
                    {index + 1}
                  </span>
                  <div className="flex-1">
                    <Label className="text-base font-medium text-slate-900">
                      {pergunta.pergunta}
                      {pergunta.obrigatoria && <span className="text-red-500 ml-1">*</span>}
                    </Label>
                  </div>
                </div>

                {/* Scale 1-5 */}
                {pergunta.tipo === 'escala_1_5' && (
                  <div className="space-y-3">
                    <div className="flex justify-between text-xs text-slate-500 px-2">
                      <span>Discordo totalmente</span>
                      <span>Concordo totalmente</span>
                    </div>
                    <div className="flex justify-between gap-2">
                      {[1, 2, 3, 4, 5].map((value) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setAnswers({ ...answers, [pergunta.id]: value })}
                          className={`flex-1 py-3 rounded-lg font-bold text-lg transition-all ${
                            answers[pergunta.id] === value
                              ? 'bg-emerald-500 text-white shadow-lg scale-105'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {value}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Scale 1-4 */}
                {pergunta.tipo === 'escala_1_4' && (
                  <div className="space-y-3">
                    <div className="flex justify-between text-xs text-slate-500 px-2">
                      <span>Discordo totalmente</span>
                      <span>Concordo totalmente</span>
                    </div>
                    <div className="flex justify-between gap-2">
                      {[1, 2, 3, 4].map((value) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setAnswers({ ...answers, [pergunta.id]: value })}
                          className={`flex-1 py-3 rounded-lg font-bold text-lg transition-all ${
                            answers[pergunta.id] === value
                              ? 'bg-emerald-500 text-white shadow-lg scale-105'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {value}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Yes/No */}
                {pergunta.tipo === 'sim_nao' && (
                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={() => setAnswers({ ...answers, [pergunta.id]: 'sim' })}
                      className={`flex-1 py-3 rounded-lg font-semibold transition-all ${
                        answers[pergunta.id] === 'sim'
                          ? 'bg-emerald-500 text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      Sim
                    </button>
                    <button
                      type="button"
                      onClick={() => setAnswers({ ...answers, [pergunta.id]: 'nao' })}
                      className={`flex-1 py-3 rounded-lg font-semibold transition-all ${
                        answers[pergunta.id] === 'nao'
                          ? 'bg-red-500 text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      Não
                    </button>
                  </div>
                )}

                {/* Free Text */}
                {pergunta.tipo === 'texto_livre' && (
                  <Textarea
                    value={answers[pergunta.id] || ''}
                    onChange={(e) => setAnswers({ ...answers, [pergunta.id]: e.target.value })}
                    placeholder="Digite sua resposta..."
                    rows={3}
                    className="w-full"
                  />
                )}

                {/* Multiple Choice */}
                {pergunta.tipo === 'multipla_escolha' && pergunta.opcoes && pergunta.opcoes.length > 0 && (
                  <div className="space-y-2">
                    {pergunta.opcoes.map((opcao, opcaoIndex) => (
                      <button
                        key={opcaoIndex}
                        type="button"
                        onClick={() => setAnswers({ ...answers, [pergunta.id]: opcao })}
                        className={`w-full p-3 rounded-lg text-left font-medium transition-all flex items-center gap-3 ${
                          answers[pergunta.id] === opcao
                            ? 'bg-emerald-500 text-white shadow-lg'
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                      >
                        <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          answers[pergunta.id] === opcao
                            ? 'border-white bg-white'
                            : 'border-slate-400'
                        }`}>
                          {answers[pergunta.id] === opcao && (
                            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                          )}
                        </span>
                        {opcao}
                      </button>
                    ))}
                  </div>
                )}
              </Card>
            ))}
          </div>

          {/* Submit Button */}
          <div className="mt-8">
            <Button
              type="submit"
              disabled={submitting}
              className="w-full py-6 text-lg bg-emerald-500 hover:bg-emerald-600"
              data-testid="submit-assessment"
            >
              {submitting ? (
                <>
                  <Loader2 size={20} className="animate-spin mr-2" />
                  Enviando...
                </>
              ) : (
                'Enviar Respostas'
              )}
            </Button>
            <p className="text-center text-xs text-slate-500 mt-4">
              Ao enviar, você confirma que suas respostas são verdadeiras e honestas.
            </p>
          </div>
        </form>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-slate-400">
          <p>NR1 Brasil - Gestão de Segurança e Saúde no Trabalho</p>
        </div>
      </div>
    </div>
  );
};

export default PublicAssessment;
